import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, query, onSnapshot, orderBy, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { generateInvoicePdf } from '../utils/invoicePdf';

const emptyForm = { quoteId: '', clientEmail: '', clientName: '', clientPhone: '', clientAddress: '', description: '', amount: '', breakdown: [] };

function toDisplayDate(val) {
  const d = val?.toDate?.() || (val instanceof Date ? val : null);
  return d ? d.toLocaleDateString('en-GB') : '';
}

export default function AdminInvoices() {
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [lineItem, setLineItem] = useState({ label: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [resendingInvoice, setResendingInvoice] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const lineItemsTotal = form.breakdown.reduce((sum, i) => sum + i.amount, 0);
  const computedTotal = form.breakdown.length > 0 ? lineItemsTotal : parseFloat(form.amount) || 0;

  useEffect(() => {
    const q1 = query(collection(db, 'quotes'), orderBy('createdAt', 'desc'));
    const q2 = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
    const u1 = onSnapshot(q1, s => setJobs(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(q2, s => setInvoices(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  useEffect(() => {
    if (location.state?.prefill) {
      const p = location.state.prefill;
      setForm({
        ...emptyForm,
        quoteId: p.quoteId || '',
        clientEmail: p.clientEmail || '',
        clientName: p.clientName || '',
        description: p.description || '',
        breakdown: p.breakdown || [],
      });
      setCreating(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  function selectJob(jobId) {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    setForm(f => ({
      ...f,
      quoteId: job.id,
      clientEmail: job.email,
      clientName: job.name,
      description: job.services?.join(', ') || 'Horticulture services',
      amount: job.quoteAmount?.toString() || '',
    }));
  }

  function addLineItem() {
    if (!lineItem.label || !lineItem.amount) return;
    setForm(f => ({ ...f, breakdown: [...f.breakdown, { label: lineItem.label, amount: parseFloat(lineItem.amount) }] }));
    setLineItem({ label: '', amount: '' });
  }

  function removeLineItem(i) {
    setForm(f => ({ ...f, breakdown: f.breakdown.filter((_, idx) => idx !== i) }));
  }

  async function resendInvoice(invoiceId) {
    setResendingInvoice(invoiceId);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch('https://europe-west2-le-douit-horticulture.cloudfunctions.net/resendCommunication', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ type: 'invoice', id: invoiceId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not resend email. Please try again.');
      }
      // Reflect the send immediately rather than waiting on the Firestore
      // listener round-trip — the function's own write will confirm this
      // moments later, but the card shouldn't look stale in the meantime.
      const sentAt = new Date();
      setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, emailSent: true, lastSentAt: sentAt } : i));
    } catch (err) {
      alert(err.message || 'Could not resend email. Please try again.');
    } finally {
      setResendingInvoice(null);
    }
  }

  async function downloadPdf(invoice) {
    setDownloadingPdf(invoice.id);
    try {
      await generateInvoicePdf(invoice);
    } catch (err) {
      alert('Could not generate the PDF. Please try again.');
    } finally {
      setDownloadingPdf(null);
    }
  }

  async function updateQuoteIfExists(quoteId, data) {
    const quoteRef = doc(db, 'quotes', quoteId);
    const quoteSnap = await getDoc(quoteRef);
    if (quoteSnap.exists()) {
      await updateDoc(quoteRef, data);
    } else {
      console.warn(`Quote ${quoteId} no longer exists — skipping status update.`);
    }
  }

  async function markPaid(invoiceId) {
    const inv = invoices.find(i => i.id === invoiceId);
    try {
      await updateDoc(doc(db, 'invoices', invoiceId), {
        paid: true,
        paidAt: serverTimestamp(),
        paidManually: true,
        paymentMethod: 'bank_transfer',
      });
      if (inv?.quoteId && !inv.recurring) {
        await updateQuoteIfExists(inv.quoteId, { status: 'paid' });
      }
    } catch (err) {
      alert('Could not mark invoice as paid. Please try again.');
    }
    setConfirming(null);
  }

  async function createInvoice(e) {
    e.preventDefault();
    const total = computedTotal;
    if (!form.clientEmail) { setError('Client email is required so the invoice can be sent.'); return; }
    if (!form.description) { setError('Please enter a job description.'); return; }
    if (!total) { setError('Please enter a total amount, or add at least one line item.'); return; }
    if (!form.quoteId && !form.clientName) { setError('Please enter the client\'s name.'); return; }

    setSaving(true);
    setError('');
    try {
      const clientEmail = form.clientEmail.trim().toLowerCase();
      let quoteId = form.quoteId || null;

      // Standalone invoice (no existing job picked): create the client/job
      // record first so it shows up in Clients and Jobs, same as any job
      // that came through the quote pathway.
      if (!quoteId) {
        const quoteRef = await addDoc(collection(db, 'quotes'), {
          name: form.clientName,
          email: clientEmail,
          phone: form.clientPhone || '',
          address: form.clientAddress || '',
          services: [],
          description: form.description,
          jobType: 'one-off',
          source: 'manual',
          status: 'invoiced',
          createdAt: serverTimestamp(),
        });
        quoteId = quoteRef.id;
      }

      const invRef = await addDoc(collection(db, 'invoices'), {
        quoteId,
        clientEmail,
        clientName: form.clientName,
        description: form.description,
        amount: total,
        breakdown: form.breakdown,
        paid: false,
        emailSent: false,
        createdAt: serverTimestamp(),
      });
      await updateDoc(invRef, { reference: `LDH-${invRef.id.slice(-6).toUpperCase()}` });

      if (form.quoteId) {
        await updateQuoteIfExists(form.quoteId, { status: 'invoiced', invoiceId: invRef.id });
      } else {
        await updateDoc(doc(db, 'quotes', quoteId), { invoiceId: invRef.id });
      }

      setCreating(false);
      setForm(emptyForm);
      setSaving(false);
    } catch (err) {
      setError(err.message || 'Failed to create invoice. Please try again.');
      setSaving(false);
    }
  }

  const completedJobs = jobs.filter(j => j.status === 'complete' || j.status === 'active');

  return (
    <>
      <div className="app-header">
        <button onClick={() => navigate('/admin/dashboard')} style={{ background: 'none', color: 'white', fontSize: '1.2rem', width: 'auto', padding: '0 12px 0 0' }}>←</button>
        <div><h1>Invoicing</h1><div className="subtitle">Create &amp; track invoices</div></div>
      </div>

      <div className="page-wrap" style={{ paddingTop: 24 }}>
        {!creating ? (
          <>
            <button className="btn-primary" style={{ marginBottom: 20 }} onClick={() => setCreating(true)}>
              + Create new invoice
            </button>

            <h2 className="section-title">Invoices</h2>
            {invoices.length === 0 && <div className="empty-state"><p>No invoices created yet.</p></div>}
            {invoices.map(inv => (
              <div key={inv.id} className="card" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{inv.clientName}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{inv.clientEmail}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 2 }}>{inv.description}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>£{inv.amount?.toFixed(2)}</div>
                    <span className={`badge badge-${inv.paid ? 'paid' : 'invoiced'}`}>
                      {inv.paid ? 'Paid' : 'Outstanding'}
                    </span>
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginTop: 6,
                  color: inv.emailSent === true ? 'var(--text-muted)' : inv.emailSent === false ? '#b4531f' : 'var(--text-muted)' }}>
                  {inv.emailSent === true
                    ? `✉️ Sent ${toDisplayDate(inv.lastSentAt)}`
                    : inv.emailSent === false
                      ? '✉️ Not yet sent'
                      : '✉️ Send status unknown (created before send tracking was added)'}
                </div>
                {inv.paid && inv.paidAt && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--green-dark)', fontWeight: 600, marginTop: 6 }}>
                    ✓ Paid {inv.paidAt?.toDate?.()?.toLocaleDateString('en-GB')}
                    {inv.paidManually ? ' · Bank transfer confirmed manually' : inv.paymentMethod === 'paypal' ? ' · PayPal' : ''}
                  </div>
                )}

                <button
                  onClick={() => resendInvoice(inv.id)}
                  disabled={resendingInvoice === inv.id}
                  style={{ marginTop: 10, width: '100%', background: 'var(--bg)', color: 'var(--text-secondary)',
                    border: '1px solid var(--border)', borderRadius: 8, padding: '9px',
                    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  {resendingInvoice === inv.id
                    ? 'Sending…'
                    : `✉️ ${inv.emailSent ? 'Resend' : 'Send'} ${inv.paid ? 'payment confirmation' : 'invoice'}`}
                </button>

                <button
                  onClick={() => downloadPdf(inv)}
                  disabled={downloadingPdf === inv.id}
                  style={{ marginTop: 8, width: '100%', background: 'var(--bg)', color: 'var(--text-secondary)',
                    border: '1px solid var(--border)', borderRadius: 8, padding: '9px',
                    fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  {downloadingPdf === inv.id ? 'Generating…' : '⬇️ Download PDF'}
                </button>

                {!inv.paid && (
                  confirming === inv.id ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
                        Confirm payment received from <strong>{inv.clientName || inv.clientEmail}</strong>?
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => markPaid(inv.id)}
                          style={{ flex: 2, background: 'var(--green-mid)', color: 'white', border: 'none',
                            borderRadius: 8, padding: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}
                        >
                          ✓ Yes, mark as paid
                        </button>
                        <button
                          onClick={() => setConfirming(null)}
                          style={{ flex: 1, background: 'white', color: 'var(--text-secondary)',
                            border: '1px solid var(--border)', borderRadius: 8, padding: '10px',
                            fontSize: '0.85rem', cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(inv.id)}
                      style={{ marginTop: 10, width: '100%', background: 'var(--green-pale)', color: 'var(--green-dark)',
                        border: '1px solid var(--green-light)', borderRadius: 8, padding: '9px',
                        fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      ✓ Mark as paid
                    </button>
                  )
                )}
              </div>
            ))}
          </>
        ) : (
          <>
            <h2 className="section-title">New invoice</h2>
            {error && <div className="error-msg">{error}</div>}
            <form onSubmit={createInvoice}>
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Link to a job (optional)</h3>
                <div className="form-group">
                  <label>Select job</label>
                  <select onChange={e => selectJob(e.target.value)} value={form.quoteId}>
                    <option value="">— Manual / standalone invoice —</option>
                    {completedJobs.map(j => (
                      <option key={j.id} value={j.id}>{j.name} — {j.address || j.services?.join(', ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Client &amp; description</h3>
                <div className="form-group">
                  <label>Client email *</label>
                  <input type="email" value={form.clientEmail} onChange={e => setForm(f => ({ ...f, clientEmail: e.target.value }))} placeholder="client@email.com" />
                </div>
                <div className="form-group">
                  <label>Client name {!form.quoteId && '*'}</label>
                  <input value={form.clientName} onChange={e => setForm(f => ({ ...f, clientName: e.target.value }))} placeholder="Full name" />
                </div>
                {!form.quoteId && (
                  <>
                    <div className="form-group">
                      <label>Client phone</label>
                      <input value={form.clientPhone} onChange={e => setForm(f => ({ ...f, clientPhone: e.target.value }))} placeholder="07…" />
                    </div>
                    <div className="form-group">
                      <label>Client address</label>
                      <input value={form.clientAddress} onChange={e => setForm(f => ({ ...f, clientAddress: e.target.value }))} placeholder="Site / billing address" />
                    </div>
                  </>
                )}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Description *</label>
                  <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Hedge cutting and lawn maintenance" />
                </div>
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>Amount</h3>
                {form.breakdown.length === 0 ? (
                  <div className="form-group">
                    <label>Total amount (£) *</label>
                    <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" min="0" step="0.01" />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Total (auto-summed from line items below)</label>
                    <div style={{ fontWeight: 800, fontSize: '1.3rem', color: 'var(--green-dark)' }}>£{computedTotal.toFixed(2)}</div>
                  </div>
                )}

                <h4 style={{ fontSize: '0.9rem', marginBottom: 12, color: 'var(--text-secondary)' }}>Line items {form.breakdown.length === 0 ? '(optional)' : ''}</h4>
                {form.breakdown.map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: '0.9rem' }}>{item.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>£{item.amount.toFixed(2)}</span>
                      <button type="button" onClick={() => removeLineItem(i)} style={{ background: 'none', color: '#C33', padding: '2px 8px', width: 'auto', fontSize: '1rem' }}>×</button>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <input value={lineItem.label} onChange={e => setLineItem(l => ({ ...l, label: e.target.value }))} placeholder="Item description" style={{ flex: 2 }} />
                  <input type="number" value={lineItem.amount} onChange={e => setLineItem(l => ({ ...l, amount: e.target.value }))} placeholder="£" style={{ flex: 1 }} min="0" step="0.01" />
                  <button type="button" onClick={addLineItem} style={{ background: 'var(--green-pale)', color: 'var(--green-dark)', border: '1px solid var(--green-light)', padding: '8px 12px', borderRadius: 8, width: 'auto', fontWeight: 700 }}>+</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn-primary" disabled={saving} style={{ flex: 2 }}>
                  {saving ? 'Creating...' : 'Create & send invoice'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setCreating(false)} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
